/* import __COLOCATED_TEMPLATE__ from './convert-to-paid-checkout.hbs'; */
/* RESPONSIBLE TEAM: team-self-serve */
import type AppService from 'embercom/services/app-service';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { keepLatestTask } from 'ember-concurrency-decorators';
import { taskFor } from 'ember-concurrency-ts';
import { INTENT_TYPES } from 'embercom/components/stripe/stripe-component';
import { post } from 'embercom/lib/ajax';
import type { DiscountDefinition } from 'embercom/lib/billing/campaign';
import {
  PERFORMANCE_PRO_BASE_ID,
  BILLING_PERIODS,
  FIN_AI_COPILOT_BASE_ID,
  PRICING_5_X_CORE_ADVANCED_ID,
  PRICING_5_X_CORE_ESSENTIAL_ID,
  PROACTIVE_SUPPORT_PLUS_BASE_ID,
} from 'embercom/lib/billing';
import { PricingModelIdentifier } from 'embercom/lib/billing/pricing-models';
import type CardlessTrialService from 'embercom/services/cardless-trial-service';
import type CompellingOfferCampaignService from 'embercom/services/compelling-offer-campaign-service';
import { DEFAULT_REQUEST_PARAMS } from 'embercom/services/quote-service';
import moment from 'moment-timezone';

import type Router from '@ember/routing/router-service';
import { type TaskGenerator } from 'ember-concurrency';
import type { AfterPaymentMethodSuccess } from 'embercom/components/stripe/stripe-component';
import { Metric } from 'embercom/models/data/pricing/metric-types';
import type Plan from 'embercom/models/plan';
import type QuoteService from 'embercom/services/quote-service';

interface Args {
  planId: number;
  billingPeriod: unknown;
  seatNumber: unknown;
}
interface Signature {
  Element: any;
  Args: Args;
}

export default class ConvertToPaidCheckout extends Component<Signature> {
  @service declare appService: AppService;
  @service declare cardlessTrialService: CardlessTrialService;
  @service declare quoteService: QuoteService;
  @service declare customerService: any;
  @service declare intl: any;
  @service declare notificationsService: any;
  @service declare store: any;
  @service declare router: Router;
  @service declare compellingOfferCampaignService: CompellingOfferCampaignService;

  @service declare purchaseAnalyticsService: any;

  @tracked billingPeriod = this.isFreeSeatsCampaign
    ? BILLING_PERIODS.Monthly
    : this.parseBillingPeriod(this.args.billingPeriod);
  // The seat number the user has selected through the input field.
  @tracked selectedSeatNumber = Number(this.args.seatNumber) || 1;
  // The actual seat number within the app but also changes if the user selects an annual plan where increases are allowed.
  // Gets reset to this.minimumSeatNumber when the user toggles back to monthly plan. See setSeatNumberToWithinMinAndMax
  @tracked seatNumber = Number(this.args.seatNumber) || 1;
  @tracked selectedProactiveAddon = false;
  @tracked copilotInitialised = false;
  @tracked selectedCopilotAdmins: string[] = [];
  @tracked hasSelectedPerformanceProAddon = false;
  @tracked showCreditCardPage = false;
  @tracked paymentMethod = '';
  @tracked selectedPlanId = String(this.args.planId || PRICING_5_X_CORE_ADVANCED_ID);

  INTENT_TYPES = INTENT_TYPES;

  constructor(owner: unknown, args: any) {
    super(owner, args);
    this.setSeatNumberToWithinMinAndMax();
    taskFor(this.getQuotes).perform();
    this.selectedCopilotAdmins = this.adminIdsWithCopilotSeats;
  }

  get app() {
    return this.appService.app;
  }

  get minimumSeatNumber() {
    let hasAssignedCoreSeats = this.appService.app.humanAdminsWithCoreSeat.length;
    let notViewingEssential = this.selectedPlanId !== PRICING_5_X_CORE_ESSENTIAL_ID;

    return Number(
      hasAssignedCoreSeats && notViewingEssential
        ? this.appService.app.humanAdminsWithCoreSeat.length
        : this.appService.app.humanAdmins.length,
    );
  }

  get maximumSeatNumber() {
    return this.billingPeriod === BILLING_PERIODS.Annual ? 100 : this.minimumSeatNumber;
  }

  get hasExistingTrial() {
    return this.customerService.customer?.inTrial;
  }

  private setSeatNumberToWithinMinAndMax() {
    if (
      this.selectedSeatNumber < this.minimumSeatNumber ||
      this.seatNumber < this.minimumSeatNumber
    ) {
      this.selectedSeatNumber = this.minimumSeatNumber;
      this.seatNumber = this.selectedSeatNumber;
    }
    if (this.seatNumber > this.maximumSeatNumber) {
      this.seatNumber = this.maximumSeatNumber;
    }
  }

  get queryParamsForRedirect() {
    return {
      plan_id: this.selectedPlanId,
      billing_period: this.billingPeriod,
      seat_number: this.seatNumber,
      proactive_addon: this.selectedProactiveAddon,
      copilot_addon: this.selectedCopilotAddon,
    };
  }

  get redirectUrl() {
    let baseUrl = window.location.origin;
    let url = this.router.urlFor('apps.app.teams-checkout', this.appService.app.id, {
      queryParams: this.queryParamsForRedirect,
    });
    return `${baseUrl}${url}`;
  }

  get adminIdsWithCopilotSeats() {
    return this.appService.app.humanAdminsWithCopilotSeat.map((admin: any) => admin.id);
  }

  get yearlyBillingPeriodSelected() {
    return this.billingPeriod === BILLING_PERIODS.Annual;
  }

  get renewalDate() {
    if (this.yearlyBillingPeriodSelected) {
      return moment().add(1, 'year');
    }
    return moment().add(1, 'month');
  }

  get renewalDayOfMonth() {
    if (this.yearlyBillingPeriodSelected) {
      return moment.localeData().ordinal(this.renewalDate.date());
    } else {
      return moment.localeData().ordinal(moment().date());
    }
  }

  get selectedPlanIds() {
    let planIds = [Number(this.selectedPlanId)];
    if (this.selectedProactiveAddon) {
      planIds.push(Number(PROACTIVE_SUPPORT_PLUS_BASE_ID));
    }
    if (this.selectedCopilotAddon) {
      planIds.push(Number(FIN_AI_COPILOT_BASE_ID));
    }
    if (this.hasSelectedPerformanceProAddon) {
      planIds.push(Number(PERFORMANCE_PRO_BASE_ID));
    }
    return planIds;
  }

  get copilotSeatNumber() {
    return this.copilotInitialised ? this.selectedCopilotAdmins.length : 0;
  }

  get selectedCopilotAddon() {
    return this.copilotSeatNumber > 0;
  }

  get sendCopilotAdminIds() {
    return this.copilotSeatNumber > 0;
  }

  get isFreeSeatsCampaign(): boolean {
    return this.compellingOfferCampaignService.isFreeSeatsCampaign;
  }

  get showDiscount() {
    return this.yearlyBillingPeriodSelected || !!this.campaignMonthlyCoreSeatDiscount;
  }

  get showCompellingOfferBanner() {
    return this.compellingOfferCampaignService.canViewGenericCampaignBanners(this.billingPeriod);
  }

  get compellingOfferBannerText() {
    return this.compellingOfferCampaignService.checkoutsBannerText;
  }

  get loading() {
    return this.quoteService.loading;
  }

  get quote() {
    let planIds = [Number(this.selectedPlanId), ...this.nonCorePlanIdsToFetch];
    return this.quoteService.getQuoteById(planIds);
  }

  get fullSeatPrice() {
    return this.quote?.fullSeatPrice(Number(this.selectedPlanId), Metric.core_seat_count) || 0;
  }

  get campaignMonthlyCoreSeatDiscount(): DiscountDefinition | undefined {
    if (this.billingPeriod !== BILLING_PERIODS.Monthly) {
      return undefined;
    }
    return this.quoteService.campaignCoreSeatDiscount(this.selectedPlanId);
  }

  get campaignDiscountedMonthlySeatPrice(): number {
    let baseTotal = this.fullSeatPrice * this.displayedSeatNumber;
    let discount = this.campaignMonthlyCoreSeatDiscount;
    if (!discount) {
      return baseTotal;
    }
    if (discount.discount_type === 'fixed') {
      return Math.max(0, baseTotal - this.quoteService.fixedDiscountInDollars(this.selectedPlanId));
    }
    return baseTotal * (1 - discount.discount_value / 100);
  }

  get discountedSeatPrice() {
    return this.quote?.discountedAnnualSeatPrice(Number(this.selectedPlanId)) || 0;
  }

  get fullProactiveAddonPrice() {
    return this.quote?.fullBreakdownAmount(Number(PROACTIVE_SUPPORT_PLUS_BASE_ID)) || 0;
  }
  get discountedProactiveAddonPrice() {
    return this.quote?.fullBreakdownAmount(Number(PROACTIVE_SUPPORT_PLUS_BASE_ID)) || 0;
  }

  get perSeatCopilotAddonPrice() {
    return (
      this.quote?.fullSeatPrice(Number(FIN_AI_COPILOT_BASE_ID), Metric.copilot_seat_count) || 0
    );
  }

  get fullCopilotAddonPrice() {
    return this.perSeatCopilotAddonPrice * this.selectedCopilotAdmins.length;
  }

  get discountedCopilotSeatPrice() {
    return (
      this.quote?.discountedAnnualSeatPrice(
        Number(FIN_AI_COPILOT_BASE_ID),
        Metric.copilot_seat_count,
      ) || 0
    );
  }

  get fullPerformanceProAddonPrice() {
    return this.quote?.fullBreakdownAmount(Number(PERFORMANCE_PRO_BASE_ID)) || 99;
  }

  get monthlyCopilotPriceForSeats() {
    let baseSeatPrice = this.yearlyBillingPeriodSelected
      ? this.discountedCopilotSeatPrice
      : this.perSeatCopilotAddonPrice;
    return baseSeatPrice * this.copilotSeatNumber;
  }

  get monthlyDiscountedTotal() {
    let total = this.monthlyDiscountedSeatPrice;
    if (this.selectedProactiveAddon) {
      total += this.fullProactiveAddonPrice;
    }
    if (this.selectedCopilotAddon) {
      total += this.monthlyCopilotPriceForSeats;
    }
    if (this.hasSelectedPerformanceProAddon) {
      total += this.fullPerformanceProAddonPrice;
    }
    return total;
  }

  get totalPrice() {
    if (this.yearlyBillingPeriodSelected) {
      return this.monthlyDiscountedTotal * 12;
    }
    if (this.campaignMonthlyCoreSeatDiscount) {
      return this.monthlyDiscountedTotal;
    }
    return this.monthlyTotal;
  }

  get totalPriceInCents() {
    return this.totalPrice * 100;
  }

  get totalPriceFormatted() {
    return this.intl.t('signup.teams.pricing5.annual-plans.basket.due-today-price', {
      yearly: this.yearlyBillingPeriodSelected,
      price: this.intl.formatNumber(this.totalPrice, {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: 0,
      }),
      htmlSafe: true,
    });
  }

  get monthlyFullSeatPrice() {
    return this.fullSeatPrice * this.displayedSeatNumber;
  }

  get monthlyDiscountedSeatPrice() {
    let discount = this.campaignMonthlyCoreSeatDiscount;
    if (this.billingPeriod === BILLING_PERIODS.Monthly && discount) {
      return this.campaignDiscountedMonthlySeatPrice;
    }
    return this.discountedSeatPrice * this.displayedSeatNumber;
  }

  get monthlyDiscountedAdditionalSeatPrice() {
    let additionalTotal = this.fullSeatPrice * this.additionalSeatNumber;
    let discount = this.campaignMonthlyCoreSeatDiscount;
    if (!discount) {
      return additionalTotal;
    }
    if (discount.discount_type === 'fixed') {
      let discountInDollars = this.quoteService.fixedDiscountInDollars(this.selectedPlanId);
      let baseTotal = this.fullSeatPrice * this.displayedSeatNumber;
      let remainingDiscount = Math.max(0, discountInDollars - baseTotal);
      return Math.max(0, additionalTotal - remainingDiscount);
    }
    return additionalTotal * (1 - discount.discount_value / 100);
  }

  get monthlyTotal() {
    let total = this.monthlyFullSeatPrice;
    if (this.selectedProactiveAddon) {
      total += this.fullProactiveAddonPrice;
    }
    if (this.selectedCopilotAddon) {
      total += this.fullCopilotAddonPrice;
    }
    if (this.hasSelectedPerformanceProAddon) {
      total += this.fullPerformanceProAddonPrice;
    }
    return total;
  }

  get backButtonLabel() {
    return this.intl.t('signup.teams.header.back');
  }

  get corePlans() {
    return this.appService.app.allPlansOnPricingModel.filter(
      (plan: Plan) => !plan.product.addon && plan.selfServe,
    );
  }

  get nonCorePlanIdsToFetch() {
    return this.appService.app.allPlansOnPricingModel
      .filter((plan: Plan) => plan.product.addon && plan.selfServe)
      .map((plan: Plan) => plan.idAsNumber);
  }

  get planIdsForQuote() {
    return this.corePlans.map((plan: Plan) => [plan.idAsNumber, ...this.nonCorePlanIdsToFetch]);
  }

  get planIdsForInitialQuote() {
    return [Number(this.selectedPlanId), ...this.nonCorePlanIdsToFetch];
  }

  get yearlySavingsAmount() {
    let coreSavings =
      (this.fullSeatPrice - this.discountedSeatPrice) * this.selectedSeatNumber * 12;
    let copilotSavings = this.selectedCopilotAddon
      ? (this.perSeatCopilotAddonPrice - this.discountedCopilotSeatPrice) *
        this.copilotSeatNumber *
        12
      : 0;
    return coreSavings + copilotSavings;
  }

  get showAnnualBillingSelector() {
    return this.appService.app.show5_1PricingModal;
  }

  get proactiveSupportBaseUsage() {
    return this.quote?.proactiveSupportBaseUsage;
  }

  get displayedSeatNumber() {
    // We always show the underlying seat number for display as monthly plans don't allow for seat increase at checkout.
    // This only governs the displayed seat number. The actual seat number is denoted by this.seatNumber & is changed at multiple places in this process.
    return this.seatNumber;
  }

  get additionalSeatNumber() {
    return this.selectedSeatNumber - this.seatNumber;
  }

  get monthlyAdditionalSeatPrice() {
    return this.fullSeatPrice * this.additionalSeatNumber;
  }

  get showAdditionalSeats() {
    return this.additionalSeatNumber > 0;
  }

  get totalCopy() {
    return this.intl.t('signup.teams.pricing5.annual-plans.basket.due-today');
  }

  backToOnboardingHomeAndOpenConversionModal() {
    let queryParams = { action: 'buy_intercom' };
    this.router.transitionTo(this.appService.app.onboardingHomeRoute, { queryParams });
  }

  @action
  onBackButtonClick() {
    if (this.showCreditCardPage) {
      this.setShowCreditCardPage();
    } else {
      this.backToOnboardingHomeAndOpenConversionModal();
    }
  }

  @action showProactiveSupportProArticle() {
    window.Intercom('showArticle', this.appService.app.proactiveSupportProArticleId);
    this.sendAnalyticsEvent({
      action: 'clicked',
      object: 'metered_proactive_support_plus_article',
    });
  }

  @action
  setSelectedCopilotAdmins(selectedAdmins: string[]) {
    this.selectedCopilotAdmins = selectedAdmins;
  }

  @action
  setCopilotAsInitialised(value: boolean) {
    this.copilotInitialised = value;
  }

  @action
  setSelectedProactiveAddon(value: boolean) {
    this.selectedProactiveAddon = value;
    this.sendAnalyticsEvent({
      action: value ? 'selected' : 'unselected',
      object: 'proactive_support_addon_selector',
    });
  }

  @action
  setHasSelectedPerformanceProAddon(value: boolean) {
    this.hasSelectedPerformanceProAddon = value;
    this.sendAnalyticsEvent({
      action: value ? 'selected' : 'unselected',
      object: 'ai_insights_addon_selector',
    });
  }

  @action
  updateBillingPeriod(value: BILLING_PERIODS) {
    this.billingPeriod = value;
    if (this.billingPeriod === BILLING_PERIODS.Annual) {
      this.seatNumber = this.selectedSeatNumber;
    }
    this.setSeatNumberToWithinMinAndMax();
    this.sendAnalyticsEvent({
      action: 'selected',
      object: 'billing_period_selector',
      context: { billing_period: `${this.billingPeriod}` },
    });
  }

  @action
  updateSelectedPlanId(plan: Plan) {
    let previousMinimum = this.minimumSeatNumber;
    this.selectedPlanId = String(plan.id);
    if (this.minimumSeatNumber !== previousMinimum && this.seatNumber === previousMinimum) {
      this.selectedSeatNumber = this.minimumSeatNumber;
      this.seatNumber = this.minimumSeatNumber;
    }
    this.setSeatNumberToWithinMinAndMax();
    this.sendAnalyticsEvent({
      action: 'selected',
      object: 'plan_selector',
      context: { plan: this.selectedPlanId },
    });
  }

  @action
  updateSeatNumber(seatNumber: number, didRemoveTeammates?: boolean) {
    let previousSeatNumber = this.seatNumber;
    this.selectedSeatNumber = seatNumber;
    this.seatNumber = this.selectedSeatNumber;
    this.setSeatNumberToWithinMinAndMax();
    // Remove any copilot selections for teammates that were just removed,
    // but preserve the user's other in-memory copilot selections
    if (didRemoveTeammates) {
      this.selectedCopilotAdmins = this.selectedCopilotAdmins.filter((adminId) =>
        this.appService.app.humanAdmins.some((admin: any) => admin.id === adminId),
      );
    }
    this.sendAnalyticsEvent({
      action: 'inputed',
      object: 'seat_number_input',
      context: {
        billing_period: this.billingPeriod,
        seat_number: this.seatNumber,
        previous_seat_number: previousSeatNumber,
        increased_seats: this.seatNumber > previousSeatNumber,
        selected_seat_number: this.selectedSeatNumber,
        additional_seat_number: this.additionalSeatNumber,
        did_add_additional_seats: this.additionalSeatNumber > 0,
      },
    });
  }

  @action
  setShowCreditCardPage() {
    this.showCreditCardPage = !this.showCreditCardPage;
    if (this.showCreditCardPage) {
      this.sendAnalyticsEvent({
        action: 'clicked',
        object: 'continue_to_payment_button',
        context: {
          plans: this.selectedPlanIds,
          billing_period: `${this.billingPeriod}`,
          seat_number: `${this.seatNumber}`,
          proactive_addon: `${this.selectedProactiveAddon}`,
        },
      });

      this.sendAnalyticsEvent({
        action: 'viewed',
        object: 'pricing_5_checkout',
      });
    }
  }

  @action sendAnalyticsEvent({
    action,
    object,
    context,
  }: {
    action: string;
    object: string;
    context?: Record<string, any>;
  }) {
    this.purchaseAnalyticsService.trackEvent({
      action,
      object,
      context,
      place: 'p5-checkout-convert-to-paid',
    });
  }

  @keepLatestTask
  *getQuotes(): TaskGenerator<void> {
    let params = this.planIdsForQuote.map((planIds: string[]) => {
      return {
        ...DEFAULT_REQUEST_PARAMS,
        planIds,
        pricingModelIdentifier: PricingModelIdentifier.PRICING_5_1,
        source: 'pricing-five-checkout',
        coreSeatCount: this.seatNumber,
      };
    });
    yield taskFor(this.quoteService.getQuotes).perform(params);
  }

  @keepLatestTask
  *convertSubscription(afterPaymentMethodSuccess: AfterPaymentMethodSuccess): TaskGenerator<void> {
    let { paymentMethod, stripeIntent, address, taxNumber } = afterPaymentMethodSuccess;
    let paramsForAnalytics = this.buildAnalyticsParams(stripeIntent);

    let params = {
      ...paramsForAnalytics,
      stripe_payment_method_id: paymentMethod,
      app_id: this.appService.app.id,
      ...this.buildNewConversionAddress(address),
      ...(this.sendCopilotAdminIds && { copilot_admin_ids: this.selectedCopilotAdmins }),
      ...(stripeIntent?.isPaymentIntent && { stripe_payment_intent_id: stripeIntent.id }),
      ...(taxNumber && { tax_number: taxNumber }),
    };

    try {
      yield post(`/ember/conversions/convert_to_paid`, params);

      this.notificationsService.notifyConfirmation(
        this.intl.t('signup.teams.pricing5.annual-plans.convert.success'),
      );
      this.router.transitionTo('apps.app.checkout.success', this.appService.app.id);
      this.sendAnalyticsEvent({
        action: 'completed',
        object: 'convert_subscription',
        context: paramsForAnalytics,
      });
    } catch (err) {
      console.error(err);

      if (err && err?.jqXHR?.responseJSON && err?.jqXHR?.responseJSON[0]) {
        this.notificationsService.notifyError(err.jqXHR.responseJSON[0]);
      } else if (err && err?.jqXHR?.responseJSON?.error) {
        this.notificationsService.notifyError(err.jqXHR.responseJSON.error);
      } else {
        this.notificationsService.notifyError(
          this.intl.t('signup.teams.pricing5.annual-plans.stripe.generic-error'),
        );
      }
      throw err;
    }
  }

  private parseBillingPeriod(billingPeriod: unknown) {
    if (Number(billingPeriod) === 1) {
      return BILLING_PERIODS.Monthly;
    }
    return BILLING_PERIODS.Annual;
  }

  private buildAnalyticsParams(stripeIntent: any) {
    return {
      billing_period_duration_in_months: this.billingPeriod,
      core_seat_count: this.seatNumber,
      selected_seat_number: this.selectedSeatNumber,
      additional_seat_number: this.additionalSeatNumber,
      did_add_additional_seats: this.additionalSeatNumber > 0,
      copilot_seat_count: this.copilotSeatNumber,
      plan_ids: this.selectedPlanIds,
      payment_intent: stripeIntent?.isPaymentIntent,
    };
  }

  private buildNewConversionAddress(address: any) {
    return {
      ...(address.streetAddress &&
        address.countryCode && {
          address: {
            country_code: address.countryCode,
            street_address: address.streetAddress,
            ...(address.stateCode && { state_code: address.stateCode }),
            ...(address.postCode && { postcode: address.postCode }),
            ...(address.city && { city: address.city }),
          },
        }),
    };
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Signup::Teams::Pricing5::Checkouts::ConvertToPaidCheckout': typeof ConvertToPaidCheckout;
  }
}
